// src/pages/CookiePolicy.jsx

import {
  Box,
  Container,
  Heading,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../components/PublicLayout";

const BRAND_RED = "#E63946";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

function Section({ number, title, children }) {
  return (
    <Box
      width="100%"
      p={{ base: 5, md: 6 }}
      bg="white"
      borderWidth="1px"
      borderColor={BRAND_BORDER}
      borderRadius="xl"
      boxShadow="sm"
    >
      <Text mb={2} color={BRAND_RED} fontSize="xs" fontWeight="extrabold" letterSpacing="wide">
        {number}
      </Text>
      <Heading as="h2" size="md" mb={3} color="gray.900">
        {title}
      </Heading>
      <Box color="gray.700" lineHeight="1.8">
        {children}
      </Box>
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <VStack align="stretch" spacing={2} mt={3} pl={2}>
      {items.map((item) => (
        <Text key={item}>• {item}</Text>
      ))}
    </VStack>
  );
}

export default function CookiePolicy() {
  return (
    <PublicLayout>
      <Helmet>
        <title>Cookie Policy | Postcode.Coupons</title>
        <meta
          name="description"
          content="How Postcode.Coupons uses cookies, browser storage and similar technologies."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box bg={BRAND_RED_LIGHT} py={{ base: 8, md: 14 }} minH="100vh">
        <Container maxW="container.md">
          <Box mb={{ base: 7, md: 10 }}>
            <Text
              color={BRAND_RED}
              fontSize="xs"
              fontWeight="extrabold"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              Postcode.Coupons
            </Text>
            <Heading as="h1" mt={2} color="gray.900" size={{ base: "xl", md: "2xl" }}>
              Cookie Policy
            </Heading>
            <Text mt={3} color="gray.600">
              Last updated: 4 October 2026
            </Text>
            <Text mt={4} color="gray.700" lineHeight="1.8">
              This policy explains how Postcode.Coupons uses cookies, browser
              storage and similar technologies when the platform is used.
            </Text>
          </Box>

          <VStack align="stretch" spacing={5}>
            <Section number="01" title="What these technologies are">
              <Text>
                Cookies are small files stored on a device. Similar technologies
                include local storage, session storage, authentication tokens and other
                methods used to store or access information in a browser or device.
              </Text>
            </Section>

            <Section number="02" title="Strictly necessary technology">
              <Text>
                Postcode.Coupons uses technology needed to operate requested platform
                features. This may include:
              </Text>
              <BulletList
                items={[
                  "Supabase authentication and session information used to keep accounts signed in and secure.",
                  "Security and fraud-prevention information used to protect accounts and platform services.",
                  "Session storage used to remember the selected postcode-map region during a browsing session.",
                  "Checkout and payment-session technology used when a payment is started through Stripe.",
                  "Load-balancing, infrastructure and technical cookies used to deliver the website reliably.",
                ]}
              />
              <Text mt={3}>
                These technologies are required for the relevant service or feature to
                work correctly.
              </Text>
            </Section>

            <Section number="03" title="Referral and preference information">
              <Text>
                Where referral links, campaign links or preferences are used, the
                platform may store a referral identifier, selected region or similar
                information so that the requested journey can continue correctly. The
                exact technology may be a cookie, local storage, session storage or a
                value passed within a link.
              </Text>
            </Section>

            <Section number="04" title="Analytics and optional technology">
              <Text>
                We will not intentionally activate non-essential analytics, profiling
                or marketing cookies without providing the information and choices
                required by applicable law. If optional technologies are added or their
                purpose changes, this policy and the platform's consent controls will be
                updated.
              </Text>
            </Section>

            <Section number="05" title="Third-party services">
              <Text>
                Platform features may involve technology provided by Supabase and
                Stripe. Those providers may process technical information necessary to
                supply authentication, infrastructure, checkout and payment services.
                Their own privacy and cookie information may also apply when their
                services are used.
              </Text>
            </Section>

            <Section number="06" title="Managing browser storage">
              <Text>
                Browser controls can be used to view, block or delete cookies and site
                data. Blocking strictly necessary technology may prevent login,
                checkout, saved preferences or other platform features from working.
                Private-browsing modes may also limit how long information is retained.
              </Text>
            </Section>

            <Section number="07" title="Changes and contact">
              <Text>
                This policy may be updated when platform technology or legal
                requirements change. Questions about cookies or browser storage can be
                sent to{" "}
                <Link href="mailto:support@postcode.coupons" color={BRAND_RED} fontWeight="bold">
                  support@postcode.coupons
                </Link>.
              </Text>
            </Section>
          </VStack>
        </Container>
      </Box>
    </PublicLayout>
  );
}
